import {
  CLERK_PRIMARY_ORIGIN,
  CLU_APP_ORIGIN,
  IS_CLERK_SATELLITE,
} from '@/components/themed-clerk-provider.component';
import { BRAND, CLU_SIGN_IN_KEY } from '@/lib/brand';
import {
  consumePostAuthRedirect,
  peekPostAuthRedirect,
} from '@/lib/post-auth-redirect';
import { useClerk, useUser } from '@clerk/react';
import { useLocation, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';

export const useAuthRedirect = () => {
  const router = useRouter();
  const clerk = useClerk();
  const { isSignedIn, isLoaded } = useUser();
  const pathname = useLocation({ select: (location) => location.pathname });
  const onAuthRoute = pathname.startsWith('/auth');

  // On the Clerk satellite (app.clugtm.com) nobody can sign in here: Clerk
  // rejects it ("not allowed on a satellite domain"). Hand off to the
  // primary's sign-in page, which sends the user back here signed in.
  useEffect(() => {
    if (!IS_CLERK_SATELLITE || !isLoaded || isSignedIn || !onAuthRoute) return;
    const redirectUrl =
      window.location.origin + (peekPostAuthRedirect() ?? '/');
    if (pathname.startsWith('/auth/register')) {
      void clerk.redirectToSignUp({ redirectUrl });
    } else {
      void clerk.redirectToSignIn({ redirectUrl });
    }
  }, [clerk, isLoaded, isSignedIn, onAuthRoute, pathname]);

  // Replace, not push: Back from the next page must not land on the auth
  // page again, which would bounce straight forward (Replay Vision).
  useEffect(() => {
    // Only from the auth page itself. While the router loads the next page
    // this one is still on screen, and remounts each time the root swaps its
    // layout: sending the user home again from there undid the onboarding
    // check's redirect, in a loop React stopped (CLEANLIST-PORTAL-9D).
    if (isLoaded && isSignedIn && onAuthRoute) {
      // A satellite sent the user here to sign in: Clerk's own sign-in sends
      // them back there. Going home first would leave them signed in here and
      // still signed out on the satellite. Clerk puts redirect_url in the
      // hash (/auth/login#/?redirect_url=…), so check both.
      const { search, hash } = window.location;
      if (/[?&]redirect_url=/.test(search + hash)) return;
      // A Clu sign-in on app.cleanlist.ai (a Canadian who came straight
      // here): their app is app.clugtm.com. A full page load, the same move
      // the Country=ca rule in public/_redirects makes on the next reload.
      if (
        BRAND.id === 'clu' &&
        window.location.origin === CLERK_PRIMARY_ORIGIN
      ) {
        // The path is same-origin (safePostAuthPath), so the host stays clugtm.
        const target = new URL(
          consumePostAuthRedirect() ?? '/',
          CLU_APP_ORIGIN
        );
        // Have the satellite pull the session just made here, as Clerk's own
        // satellite sign-in does (and the ticket hand-off).
        target.searchParams.set('__clerk_synced', 'false');
        // The brand is remembered only to survive the SSO round trip: a later
        // sign-in in this tab starts fresh.
        try {
          sessionStorage.removeItem(CLU_SIGN_IN_KEY);
        } catch {
          // Storage blocked: nothing was remembered.
        }
        window.location.assign(target.toString());
        return;
      }
      // The history, not navigate({ to }): `to` is a route path, and the
      // remembered page carries its query (a shared finder search's ?q=).
      router.history.replace(consumePostAuthRedirect() ?? '/');
    }
  }, [isSignedIn, isLoaded, onAuthRoute, router]);
};
